import React, { useEffect, useState } from "react";
import api from "../api/api"; // axios instance with baseURL and auth headers
import { useNavigate } from "react-router-dom";
import { isProfileComplete } from "../utils/profileHelper";



const TIME_ZONES = ["EST", "PST", "CST", "MST", "GMT", "IST"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function ProfileSetup() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    fullName: "",
    phone: "",
    city: "",
    country: "",
    title: "",
    yearsExperience: "",
    skills: "",
    preferredRoles: "",
    willingToRelocate: false,
    expectedSalaryMin: "",
    expectedSalaryMax: "",
    resume: null,
    avatar: null,
    education: [],
    workExperience: [],
    interviewPreferences: {
      timeZone: "",
      availableDays: [],
      preferredInterviewers: [""],
    },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [resumeName, setResumeName] = useState(null);
  
  useEffect(() => {
  async function fetchProfile() {
    try {
      const res = await api.get("/profile");
      const data = res.data;

      setProfile({
        fullName: data.fullName || "",
        phone: data.phone || "",
        city: data.city || "",
        country: data.country || "",
        title: data.title || "",
        yearsExperience: data.yearsExperience ?? "", // allow 0 or number
        skills: data.skills || "",
        preferredRoles: data.preferredRoles || "",
        willingToRelocate: data.willingToRelocate || false,
        expectedSalaryMin: data.expectedSalaryMin || "",
        expectedSalaryMax: data.expectedSalaryMax || "",
        resume: null,
        avatar: null,
        education: data.education || [],
        workExperience: data.workExperience || [],
        interviewPreferences: {
  timeZone: data.interviewPreferences?.timeZone || "",
  availableDays: data.interviewPreferences?.availableDays || [], // <-- important
  preferredInterviewers: data.interviewPreferences?.preferredInterviewers || [""],
},

      });

      if (data.avatar) setAvatarPreview(`${process.env.REACT_APP_API_URL}/uploads/avatar/${data.avatar}`);

      if (data.resume) setResumeName(data.resume);
    } catch (err) {
      if (!(err.response && err.response.status === 404)) {
        setError("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  }
  fetchProfile();
}, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleInterviewPrefChange = (e) => {
    const { name, value, checked } = e.target;
    setProfile((prev) => {
      const ip = { ...prev.interviewPreferences };
      if (name === "timeZone") {
        ip.timeZone = value;
      } else if (name.startsWith("availableDays")) {
        const day = value;
        if (checked) {
          ip.availableDays = [...ip.availableDays, day];
        } else {
          ip.availableDays = ip.availableDays.filter((d) => d !== day);
        }
      } else if (name.startsWith("preferredInterviewers")) {
        const idx = Number(name.split(".")[1]);
        ip.preferredInterviewers[idx] = value;
      }
      return { ...prev, interviewPreferences: ip };
    });
  };

  const addPreferredInterviewer = () => {
    setProfile((prev) => ({
      ...prev,
      interviewPreferences: {
        ...prev.interviewPreferences,
        preferredInterviewers: [...prev.interviewPreferences.preferredInterviewers, ""],
      },
    }));
  };

  const removePreferredInterviewer = (index) => {
    setProfile((prev) => {
      const newList = prev.interviewPreferences.preferredInterviewers.filter((_, i) => i !== index);
      return {
        ...prev,
        interviewPreferences: {
          ...prev.interviewPreferences,
          preferredInterviewers: newList.length ? newList : [""],
        },
      };
    });
  };

  const handleEducationChange = (index, field, value) => {
    setProfile((prev) => {
      const ed = [...prev.education];
      ed[index] = { ...ed[index], [field]: value };
      return { ...prev, education: ed };
    });
  };

  const addEducation = () => {
    setProfile((prev) => ({
      ...prev,
      education: [...prev.education, { year: "", degree: "", institution: "" }],
    }));
  };

  const removeEducation = (index) => {
    setProfile((prev) => {
      const ed = prev.education.filter((_, i) => i !== index);
      return { ...prev, education: ed };
    });
  };

  const handleWorkExpChange = (index, field, value) => {
    setProfile((prev) => {
      const work = [...prev.workExperience];
      work[index] = { ...work[index], [field]: value };
      return { ...prev, workExperience: work };
    });
  };

  const addWorkExp = () => {
    setProfile((prev) => ({
      ...prev,
      workExperience: [...prev.workExperience, { role: "", years: "", company: "" }],
    }));
  };

  const removeWorkExp = (index) => {
    setProfile((prev) => {
      const work = prev.workExperience.filter((_, i) => i !== index);
      return { ...prev, workExperience: work };
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length === 0) return;
    const file = files[0];

    setProfile((prev) => ({
      ...prev,
      [name]: file,
    }));

    if (name === "avatar") {
      setAvatarPreview(URL.createObjectURL(file));
    }
    if (name === "resume") {
      setResumeName(file.name);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  setError(null);

  try {
    const formData = new FormData();

    // Basic fields
    formData.append("fullName", profile.fullName);
    formData.append("phone", profile.phone || "");
    formData.append("city", profile.city || "");
    formData.append("country", profile.country || "");
    formData.append("title", profile.title || "");
    formData.append("skills", profile.skills || "");
    formData.append("preferredRoles", profile.preferredRoles || "");
    formData.append("willingToRelocate", profile.willingToRelocate);

    // Numeric fields: convert empty string/null to 0
    formData.append("yearsExperience", profile.yearsExperience ? Number(profile.yearsExperience) : 0);
    formData.append("expectedSalaryMin", profile.expectedSalaryMin ? Number(profile.expectedSalaryMin) : 0);
    formData.append("expectedSalaryMax", profile.expectedSalaryMax ? Number(profile.expectedSalaryMax) : 0);

    // Files
    if (profile.resume instanceof File) formData.append("resume", profile.resume);
    if (profile.avatar instanceof File) formData.append("avatar", profile.avatar);

    // Arrays / Objects
    formData.append("education", JSON.stringify(profile.education || []));
    formData.append("workExperience", JSON.stringify(profile.workExperience || []));
    formData.append(
      "interviewPreferences",
      JSON.stringify(profile.interviewPreferences || { timeZone: "", availableDays: [], preferredInterviewers: [""] })
    );

    await api.put("/profile", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    console.log("Profile saved successfully, navigating to dashboard...");
    navigate("/dashboard");
    window.location.reload();
  } catch (err) {
    console.error("Failed to save profile:", err);
    setError(err.response?.data?.message || "Failed to save profile");
  } finally {
    setSaving(false);
  }
};


  if (loading) return <p className="p-6 text-center">Loading profile...</p>;

  return (
    <div className="max-w-5xl mx-auto p-8 bg-white rounded-lg shadow mt-10">
      <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">Complete Your Profile</h2>

      {error && <div className="mb-6 p-4 bg-red-100 text-red-700 rounded">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-10">
        {/* Basic Info */}
        <section>
          <h3 className="text-xl font-semibold mb-4 text-indigo-700">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label className="block">
              <span className="text-gray-700 font-semibold">Full Name *</span>
              <input
                name="fullName"
                type="text"
                required
                value={profile.fullName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">Phone</span>
              <input
                name="phone"
                type="tel"
                value={profile.phone}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">City</span>
              <input
                name="city"
                type="text"
                value={profile.city}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">Country</span>
              <input
                name="country"
                type="text"
                value={profile.country}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">Professional Title</span>
              <input
                name="title"
                type="text"
                value={profile.title}
                onChange={handleChange}
                placeholder="e.g. Senior Developer"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">Years of Experience</span>
              <input
                name="yearsExperience"
                type="number"
                min="0"
                step="0.1"
                value={profile.yearsExperience}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
        </section>

        {/* Skills & Roles */}
        <section>
          <h3 className="text-xl font-semibold mb-4 text-indigo-700">Skills & Roles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label className="block">
              <span className="text-gray-700 font-semibold">Skills (comma separated)</span>
              <input
                name="skills"
                type="text"
                value={profile.skills}
                onChange={handleChange}
                placeholder="JavaScript, React, Node.js"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">Preferred Roles (comma separated)</span>
              <input
                name="preferredRoles"
                type="text"
                value={profile.preferredRoles}
                onChange={handleChange}
                placeholder="Backend Developer, Full Stack Developer"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
          <div className="flex items-center space-x-3 mt-4">
            <input
              id="willingToRelocate"
              name="willingToRelocate"
              type="checkbox"
              checked={profile.willingToRelocate}
              onChange={handleChange}
              className="w-5 h-5"
            />
            <label htmlFor="willingToRelocate" className="font-semibold text-gray-700">
              Willing to Relocate
            </label>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-4">
            <label className="block">
              <span className="text-gray-700 font-semibold">Expected Salary Min ($)</span>
              <input
                name="expectedSalaryMin"
                type="number"
                min="0"
                value={profile.expectedSalaryMin}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-gray-700 font-semibold">Expected Salary Max ($)</span>
              <input
                name="expectedSalaryMax"
                type="number"
                min="0"
                value={profile.expectedSalaryMax}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
        </section>

        {/* Resume & Avatar */}
        <section>
          <h3 className="text-xl font-semibold mb-4 text-indigo-700">Upload Resume & Avatar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <label className="block font-semibold mb-1 text-gray-700" htmlFor="resume">
                Resume (PDF, DOCX)
              </label>
              <input
                type="file"
                id="resume"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="block w-full text-gray-700"
              />
              {resumeName && <p className="mt-2 text-sm text-gray-600">Selected file: {resumeName}</p>}
            </div>
            <div>
              <label className="block font-semibold mb-1 text-gray-700" htmlFor="avatar">
                Avatar (Image)
              </label>
              <input
                type="file"
                id="avatar"
                name="avatar"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-gray-700"
              />
              {avatarPreview && (
                <img
                  src={avatarPreview}
                  alt="Avatar Preview"
                  className="mt-2 h-24 w-24 rounded-full object-cover border"
                />
              )}
            </div>
          </div>
        </section>

        {/* Education */}
        <section>
          <h3 className="text-xl font-semibold mb-4 text-indigo-700">Education</h3>
          {profile.education.length === 0 && (
            <p className="mb-4 text-gray-600">Add your education details below.</p>
          )}
          {profile.education.map((edu, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 items-end">
              <label className="block">
                <span className="text-gray-700 font-semibold">Year</span>
                <input
                  type="text"
                  value={edu.year}
                  onChange={(e) => handleEducationChange(i, "year", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. 2018"
                />
              </label>
              <label className="block">
                <span className="text-gray-700 font-semibold">Degree</span>
                <input
                  type="text"
                  value={edu.degree}
                  onChange={(e) => handleEducationChange(i, "degree", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. BSc Computer Science"
                />
              </label>
              <label className="block">
                <span className="text-gray-700 font-semibold">Institution</span>
                <input
                  type="text"
                  value={edu.institution}
                  onChange={(e) => handleEducationChange(i, "institution", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. University of XYZ"
                />
              </label>
              <button
                type="button"
                onClick={() => removeEducation(i)}
                className="text-red-600 hover:text-red-800 font-semibold"
                aria-label={`Remove education entry ${i + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addEducation}
            className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Add Education
          </button>
        </section>

        {/* Work Experience */}
        <section>
          <h3 className="text-xl font-semibold mb-4 text-indigo-700">Work Experience</h3>
          {profile.workExperience.length === 0 && (
            <p className="mb-4 text-gray-600">Add your work experience below.</p>
          )}
          {profile.workExperience.map((work, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 items-end">
              <label className="block">
                <span className="text-gray-700 font-semibold">Role</span>
                <input
                  type="text"
                  value={work.role}
                  onChange={(e) => handleWorkExpChange(i, "role", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Software Engineer"
                />
              </label>
              <label className="block">
                <span className="text-gray-700 font-semibold">Years</span>
                <input
                  type="text"
                  value={work.years}
                  onChange={(e) => handleWorkExpChange(i, "years", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. 3"
                />
              </label>
              <label className="block">
                <span className="text-gray-700 font-semibold">Company</span>
                <input
                  type="text"
                  value={work.company}
                  onChange={(e) => handleWorkExpChange(i, "company", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. ABC Corp"
                />
              </label>
              <button
                type="button"
                onClick={() => removeWorkExp(i)}
                className="text-red-600 hover:text-red-800 font-semibold"
                aria-label={`Remove work experience entry ${i + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addWorkExp}
            className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Add Work Experience
          </button>
        </section>

        {/* Interview Preferences */}
        <section>
          <h3 className="text-xl font-semibold mb-4 text-indigo-700">Interview Preferences</h3>
          <label className="block mb-4">
            <span className="text-gray-700 font-semibold">Time Zone</span>
            <select
              name="timeZone"
              value={profile.interviewPreferences.timeZone}
              onChange={handleInterviewPrefChange}
              className="mt-1 block w-48 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a time zone</option>
              {TIME_ZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="mb-4">
            <legend className="text-gray-700 font-semibold mb-2">Available Days</legend>
            <div className="flex flex-wrap gap-4">
              {DAYS.map((day) => (
                <label key={day} className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="availableDays"
                    value={day}
                    checked={profile.interviewPreferences.availableDays.includes(day)}
                    onChange={handleInterviewPrefChange}
                    className="w-5 h-5"
                  />
                  <span>{day}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-gray-700 font-semibold mb-2">Preferred Interviewers</legend>
            {profile.interviewPreferences.preferredInterviewers.map((interviewer, i) => (
              <div key={i} className="flex items-center space-x-4 mb-3">
                <input
                  type="text"
                  name={`preferredInterviewers.${i}`}
                  value={interviewer}
                  onChange={handleInterviewPrefChange}
                  placeholder="Interviewer name"
                  className="flex-grow rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => removePreferredInterviewer(i)}
                  className="text-red-600 hover:text-red-800 font-semibold"
                  aria-label={`Remove preferred interviewer ${i + 1}`}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPreferredInterviewer}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Add Interviewer
            </button>
          </fieldset>
        </section>
{/* Warning if profile is incomplete */}


        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </form>
    </div>
  );
}

export default ProfileSetup;
